import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaFacebook, FaApple, FaGoogle } from 'react-icons/fa';
import { fetchWithFallback } from '../../services/apiClient';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import loginImage from '../../assets/backgrounds/login.png';
import './styles.css';

// Base da API com fallback para produção e filtro de localhost
const ENV_API = String(import.meta.env?.VITE_URL_API || '').trim();
const DEV_MODE = String(import.meta.env?.VITE_DEV_MODE || 'false').toLowerCase() === 'true';
const IS_LOCAL = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(ENV_API);
const API_BASE = ENV_API || 'https://servicesapi.joviplanet.com/api';

function getSafeRedirect(search) {
  const params = new URLSearchParams(search);
  const redirect = params.get('redirect');
  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
    return redirect;
  }
  return '/dashboard';
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [socialLoginDisabled, setSocialLoginDisabled] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    document.body.classList.add('login-page');
    return () => {
      document.body.classList.remove('login-page');
    };
  }, []);

  // Normaliza o objeto de usuário para garantir que tenha pelo menos id ou email
  function normalizeUser(u, emailFromForm) {
    const src = (u && typeof u === 'object') ? u : {};
    const id = src.id || src.idUSUARIO || src._id || src.usuarioId || src.userId;

    const candidates = [src.email, src.emailUSUARIO, emailFromForm];
    let email = '';
    for (const c of candidates) {
      if (typeof c === 'string' && c.includes('@')) { email = c; break; }
    }
    return {
      ...src,
      ...(id ? { id } : {}),
      ...(email ? { email } : {}),
    };
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Tenta todos os formatos de payload conhecidos para login
    const payloads = [
      { emailUSUARIO: formData.email, senhaUSUARIO: formData.password },
      { email: formData.email, senha: formData.password },
      { usuario: formData.email, senha: formData.password },
      { emailUSUARIO: formData.email, senhaUSUARIO: formData.password, email: formData.email, senha: formData.password },
    ];
    let loggedIn = false;
    let lastError = null;
    for (let i = 0; i < payloads.length; i++) {
      try {
        const response = await fetch(`${API_BASE}/usuarios/login`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payloads[i]),
        });
        const data = await response.json();
        if (response.ok && data.status === 1 && data.token) {
          localStorage.setItem('auth_token', data.token);
          // Salva o usuário no localStorage (ajuste o campo conforme a resposta da sua API)
          if (data.usuario || data.user || data.dados) {
            const rawUser = data.usuario || data.user || data.dados;
            const normalized = normalizeUser(rawUser, formData.email);
            localStorage.setItem('user', JSON.stringify(normalized));
          }
          navigate(getSafeRedirect(location.search));
          loggedIn = true;
          break;
        } else {
          lastError = data.mensagem || 'Email ou senha inválidos. Por favor, tente novamente.';
        }
      } catch (err) {
        lastError = 'Erro ao conectar com o servidor. Tente novamente.';
      }
    }
    if (!loggedIn) {
      setError(lastError);
    }
    setLoading(false);

  };

  // Loader do Google Identity
  let googleScriptPromise = null;
  const loadGoogleScript = () => {
    if (googleScriptPromise) return googleScriptPromise;
    googleScriptPromise = new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) return resolve();
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Falha ao carregar Google Identity'));
      document.body.appendChild(s);
    });
    return googleScriptPromise;
  };

  const handleGoogleLogin = async () => {
    try {
      setSocialLoginDisabled(true);
      setGoogleLoading(true);
      setError(null);

      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID_LOGIN || import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) throw new Error('Defina VITE_GOOGLE_CLIENT_ID_LOGIN no .env');

      await loadGoogleScript();
      if (!window.google?.accounts?.id) throw new Error('Google Identity indisponível');

      const socialToken = await new Promise((resolve, reject) => {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => resp?.credential ? resolve(resp.credential) : reject(new Error('Não foi possível obter o credential Google'))
        });
        window.google.accounts.id.prompt((notif) => {
          if (notif.isNotDisplayed?.() || notif.isSkippedMoment?.()) {
            reject(new Error('Login Google cancelado ou não exibido.'));
          }
        });
      });

      const resp = await fetchWithFallback('/usuarios/social-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json'
        },
        body: JSON.stringify({ provider: 'google', providerId: '', token: socialToken })
      });

      const raw = await resp.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch (_) { data = {}; }

      if (!resp.ok) {
        const msg = data?.mensagem || data?.message || data?.error || raw || 'Falha no login social';
        throw new Error(msg);
      }

      const token = data?.token || data?.auth_token || data?.access_token;
      const userData = data?.usuario || data?.user || data?.dados;
      if (token) localStorage.setItem('auth_token', token);
      if (userData) {
        const normalized = normalizeUser(userData, formData.email);
        localStorage.setItem('user', JSON.stringify(normalized));
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('[login-page] Erro login Google', err);
      setError(err?.message || 'Erro ao fazer login com Google');
    } finally {
      setGoogleLoading(false);
      setSocialLoginDisabled(false);
    }
  };

  return (
    <>
      <Header />
      <section className="login-section">
        <div className="login-container">
          <div className="login-content">
            <div className="login-left">
              <div className="login-form-container">
                <div className="social-login">
                  <button type="button" className="social-button facebook" disabled>
                    <FaFacebook /> <span>Continuar com o Facebook</span>
                  </button>
                  <button type="button" className="social-button apple" disabled>
                    <FaApple /> <span>Continuar com a Apple</span>
                  </button>
                  <button
                    type="button"
                    className="social-button google"
                    onClick={handleGoogleLogin}
                    disabled={socialLoginDisabled || googleLoading}
                  >
                    <FaGoogle /> <span>{googleLoading ? 'Conectando...' : 'Continuar com o Google'}</span>
                  </button>
                </div>

                <div className="divider">
                  <span>ou</span>
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group password-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="form-input"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <button 
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>

                  <div className="form-options">
                    <label className="remember-me">
                      <input
                        type="checkbox"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                      />
                      <span>Lembrar-me</span>
                    </label>
                    <Link to="/forgot-password" className="forgot-password">
                      Esqueceu-se da senha?
                    </Link>
                  </div>

                  <button 
                    type="submit" 
                    className={`submit-button ${loading ? 'loading' : ''}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="button-loader"></span>
                        Entrando...
                      </>
                    ) : (
                      'Entrar'
                    )}
                  </button>
                </form>

                <div className="login-footer">
                  <p>
                    Não tem conta? <Link to="/register">Registre-se</Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="login-right">
              <div className="login-info">
                <h2>Milhões de compradores e vendedores confiam em nós</h2>
                <ul className="benefits-list">
                  <li>Uma das maiores plataformas imobiliárias internacionais</li>
                  <li>Mais de 6 milhões de visitas por mês</li>
                  <li>Mais de 250 mil anúncios ativos por mês</li>
                  
                  <li>Ferramentas para ajudar a alcançar os seus objetivos</li>
                </ul>
              </div>
              <img src={loginImage} alt="Login" className="login-image" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Login; 